import { useState, useCallback } from 'react'
import moment from 'moment-timezone'
import { getSchedulableDatesFromList } from '@abroad/rest-client'
import API from '../../../utils/API'

export const useScheduleSubmission = (
  schedulingType,
  sowId,
  sowSessionDuration,
  timezone,
  convertDateAndSlotToUTC,
  onScheduleSuccess,
  runScheduling,
  isConditionalSchedulingEnabled = false,
) => {
  const [isScheduling, setIsScheduling] = useState(false)

  const handleSchedule = useCallback(
    async (selectedDate, selectedSlot, selectedDatesList) => {
      if (schedulingType === 'schedule-all') {
        if (!selectedDatesList || selectedDatesList.length === 0) {
          throw new Error('Please select dates for scheduling')
        }
      } else {
        if (!selectedDate || !selectedSlot) {
          throw new Error('Please select a date and time slot')
        }
      }

      if (!sowId) {
        throw new Error(
          'Unable to find coaching plan details. Please try again later.',
        )
      }

      if (!runScheduling) {
        throw new Error('Scheduling is not available. Please try again.')
      }

      try {
        setIsScheduling(true)

        if (schedulingType === 'first-session') {
          const { start: slotStartMoment, end: slotEndFromConversion } =
            convertDateAndSlotToUTC(
              selectedDate,
              selectedSlot.start,
              selectedSlot.end,
            )

          const sessionDurationMinutes = Number(sowSessionDuration) || 0
          const slotEndMoment = sessionDurationMinutes
            ? slotStartMoment.clone().add(sessionDurationMinutes, 'minutes')
            : slotEndFromConversion || slotStartMoment.clone()

          const payload = {
            slotStartTime: slotStartMoment.toISOString(),
            slotEndTime: slotEndMoment.toISOString(),
            sowId,
          }

          const result = await runScheduling(
            (schedulePayload) =>
              API.ClientAPI.session.scheduleFirstSession(schedulePayload),
            payload,
          )

          if (!result?.success) {
            return result
          }

          return {
            success: true,
            message: 'Your first session has been scheduled!',
          }
        } else if (schedulingType === 'remaining') {
          const { start: slotStartMoment } = convertDateAndSlotToUTC(
            selectedDate,
            selectedSlot.start,
          )

          const payload = {
            slotStartTime: slotStartMoment.toISOString(),
            sowId,
          }

          const { data } =
            await API.ClientAPI.session.scheduleRemainingSessions(payload)

          if (!data?.success) {
            throw new Error(
              data?.message ||
                'Unable to schedule your sessions. Please try again.',
            )
          }

          if (onScheduleSuccess) {
            onScheduleSuccess(data?.event)
          }

          return {
            success: true,
            message: 'Your remaining sessions have been scheduled!',
          }
        } else if (schedulingType === 'schedule-all') {
          const availableDates = getSchedulableDatesFromList(selectedDatesList)

          if (availableDates.length === 0) {
            throw new Error(
              'No available slots found. Please select different dates.',
            )
          }

          const timeSlotsPayload = availableDates.map((dateItem) => {
            const startMoment = moment.utc(dateItem.slotStart)
            // Growth slots come back from availability with their own duration
            const durationMinutes =
              Number(dateItem.duration) || Number(sowSessionDuration) || 0
            const endMoment = durationMinutes
              ? startMoment.clone().add(durationMinutes, 'minutes')
              : dateItem.slotEnd
              ? moment.utc(dateItem.slotEnd)
              : startMoment.clone()

            const payloadItem = {
              date: dateItem.date,
              startTime: startMoment.toISOString(),
              endTime: endMoment.toISOString(),
            }

            // Prefer the real sessionIndex from conditional/remaining flows
            // (e.g. remaining session 2 must stay 2, not become array index + 1).
            // Only sent when conditional scheduling is on
            if (
              isConditionalSchedulingEnabled &&
              dateItem.sessionIndex !== undefined &&
              dateItem.sessionIndex !== null
            ) {
              payloadItem.sessionIndex = Number(dateItem.sessionIndex)
            }

            return payloadItem
          })

          const payload = {
            sowId,
            timeSlots: timeSlotsPayload,
          }

          if (timezone) {
            payload.clientTimezone = timezone
          }

          const result = await runScheduling(
            (schedulePayload) =>
              API.ClientAPI.session.scheduleAllSessions(schedulePayload),
            payload,
          )

          if (!result?.success) {
            return result
          }

          return {
            success: true,
            message:
              result.data?.message ||
              'Your sessions have been scheduled successfully!',
          }
        }
      } finally {
        setIsScheduling(false)
      }
    },
    [
      schedulingType,
      sowId,
      sowSessionDuration,
      timezone,
      convertDateAndSlotToUTC,
      onScheduleSuccess,
      runScheduling,
      isConditionalSchedulingEnabled,
    ],
  )

  return { handleSchedule, isScheduling }
}
